import { useState, useEffect, useMemo } from 'react';
import { announcementsApi, ApiRequestError, type AnnouncementRecipient } from '@/lib/api';

const ITEMS_PER_PAGE = 15; // 5 rows x 3 columns

export default function AnnouncementsPage() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [recipients, setRecipients] = useState<AnnouncementRecipient[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    success: boolean;
    totalRecipients: number;
    successCount: number;
    failureCount: number;
    failures?: Array<{ email: string; error: string }>;
  } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Load recipients on mount and when includeInactive changes
  useEffect(() => {
    const loadRecipients = async () => {
      setIsLoading(true);
      try {
        const data = await announcementsApi.getRecipients(includeInactive);
        setRecipients(data.recipients);
        // Select all by default
        setSelectedIds(new Set(data.recipients.map((r) => r.id)));
        setCurrentPage(1);
      } catch (err) {
        console.error('Failed to load recipients:', err);
        setError('Failed to load recipients');
      } finally {
        setIsLoading(false);
      }
    };
    loadRecipients();
  }, [includeInactive]);

  // Pagination
  const totalPages = Math.ceil(recipients.length / ITEMS_PER_PAGE);
  const paginatedRecipients = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return recipients.slice(start, start + ITEMS_PER_PAGE);
  }, [recipients, currentPage]);

  const toggleRecipient = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(recipients.map((r) => r.id)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const handleSendTest = async () => {
    setError(null);
    setResult(null);

    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    setIsSending(true);
    try {
      const data = await announcementsApi.send({
        subject: subject.trim(),
        message: message.trim(),
        testMode: true,
      });
      setResult(data);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to send test email');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSelected = async () => {
    setError(null);
    setResult(null);
    setShowConfirm(false);

    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }
    if (selectedIds.size === 0) {
      setError('No recipients selected');
      return;
    }

    setIsSending(true);
    try {
      const data = await announcementsApi.send({
        subject: subject.trim(),
        message: message.trim(),
        userIds: Array.from(selectedIds),
        testMode: false,
      });
      setResult(data);

      // Clear form on success
      if (data.success && data.failureCount === 0) {
        setSubject('');
        setMessage('');
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to send announcement');
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Send Announcement</h1>
      <p className="text-gray-600 mb-6">
        Send an email to selected users. Use this for important updates, maintenance notifications,
        or announcements.
      </p>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="e.g., Important: Security Update"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">{subject.length}/200 characters</p>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={8}
            placeholder="Enter your message here. Line breaks will be preserved."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          />
        </div>

        {/* Recipients Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Recipients ({selectedIds.size} of {recipients.length} selected)
            </label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeInactive"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeInactive" className="text-sm text-gray-600">
                  Include inactive
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Select all
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Select none
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-3">
            To remove someone from receiving the mail, simply switch them to the off position.
          </p>

          {/* Recipients Grid */}
          {isLoading ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
              Loading recipients...
            </div>
          ) : recipients.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
              No recipients found
            </div>
          ) : (
            <>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                  {paginatedRecipients.map((recipient) => (
                    <label
                      key={recipient.id}
                      className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-100 rounded px-2 -mx-2"
                    >
                      <button
                        type="button"
                        role="switch"
                        aria-checked={selectedIds.has(recipient.id)}
                        onClick={() => toggleRecipient(recipient.id)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          selectedIds.has(recipient.id) ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            selectedIds.has(recipient.id) ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span
                        className={`text-sm truncate ${
                          recipient.is_active ? 'text-gray-900' : 'text-gray-500 italic'
                        }`}
                        title={recipient.name}
                      >
                        {recipient.name}
                        {!recipient.is_active && ' (inactive)'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div
            className={`rounded-lg p-4 ${
              result.failureCount === 0
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <h3
              className={`font-medium ${
                result.failureCount === 0 ? 'text-green-800' : 'text-yellow-800'
              }`}
            >
              {result.failureCount === 0 ? 'Emails Sent Successfully!' : 'Emails Sent with Some Failures'}
            </h3>
            <div className="mt-2 text-sm space-y-1">
              <p className={result.failureCount === 0 ? 'text-green-700' : 'text-yellow-700'}>
                Sent: {result.successCount} of {result.totalRecipients}
              </p>
              {result.failureCount > 0 && (
                <p className="text-yellow-700">Failed: {result.failureCount}</p>
              )}
              {result.failures && result.failures.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium text-yellow-800">Failed recipients:</p>
                  <ul className="mt-1 list-disc list-inside">
                    {result.failures.map((f, i) => (
                      <li key={i} className="text-yellow-700">
                        {f.email}: {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleSendTest}
            disabled={isSending || !subject.trim() || !message.trim()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending...' : 'Send Test Email to Me'}
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={isSending || !subject.trim() || !message.trim() || selectedIds.size === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send to Selected ({selectedIds.size})
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Confirm Send</h2>
            <p className="text-gray-600 mb-4">
              You are about to send this announcement to{' '}
              <strong>
                {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''}
              </strong>
              .
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-700">Subject:</p>
              <p className="text-sm text-gray-900">{subject}</p>
            </div>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendSelected}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSending ? 'Sending...' : 'Yes, Send to Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Effective Announcements</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Use a clear, descriptive subject line</li>
          <li>Keep the message concise and action-oriented</li>
          <li>Always send a test email to yourself first</li>
          <li>Use line breaks to separate paragraphs for readability</li>
        </ul>
      </div>
    </div>
  );
}
